// =============================================================================
// COLOR CONSTANTS
// =============================================================================
// TypeScript constants for colors used in React components
// Colors that match design tokens from _design-tokens.scss are named to match the token name
// Use these in TypeScript/TSX files instead of hardcoded color values

// =============================================================================
// OTHER UNKNOWN COLORS MATCHING DESIGN TOKENS
// =============================================================================

export const UNKNOWN_ACTIVE_GREEN = '#24b040';
export const UNKNOWN_PENDING_ORANGE = 'darkorange';
export const UNKNOWN_TEXT_INACTIVE = '#6a7581';
export const UNKNOWN_ANT_ORANGE = '#faad14';
export const UNKNOWN_CT_BLUE = '#1366a6';
export const UNKNOWN_CT_ORANGE = '#f6931e';
export const UNKNOWN_GRAY_f7f7f6 = '#f7f7f6';
export const UNKNOWN_GRAY_888 = '#888888';
export const UNKNOWN_TEXT_DARK_BLUE = '#4B535E';
export const UNKNOWN_BLACK_1a1a1a = '#1a1a1a';

// =============================================================================
// OTHER COLORS NOT MATCHING DESIGN TOKENS
// =============================================================================

export const UNKNOWN_BACKGROUND_RED = '#C60A0D'; // Red background (close to --Primary-5)
export const UNKNOWN_CHART_RED = '#ed3426'; // Red for charts
export const UNKNOWN_CHART_BLUE_ALT = '#0164a8'; // Alternative blue for charts
export const UNKNOWN_FEEDBACK_SUCCESS = '#30BF78'; // Green for positive feedback
export const UNKNOWN_FEEDBACK_ERROR = '#F4664A'; // Red/orange for negative feedback
export const UNKNOWN_CT_BLUE_OPACITY_15 = 'rgba(19, 102, 166, 0.15)'; // 15% opacity CT Blue

// =============================================================================
// UI LIBRARY COLOR PALETTE
// =============================================================================

export const WHITE = '#FFFFFF';
export const BLACK = '#000000';
export const GRAY_25 = '#FCFCFD';
export const GRAY_50 = '#F9FAFB';
export const GRAY_100 = '#F2F2F2';
export const GRAY_200 = '#EBEBEB';
export const GRAY_300 = '#D9D9D9';
export const GRAY_400 = '#AAAAAA';
export const GRAY_500 = '#767676';
export const GRAY_600 = '#585858';
export const GRAY_700 = '#454545';
export const GRAY_800 = '#232323';
export const GRAY_900 = '#222222';
export const GRAY_950 = '#151515';

export const RED_25 = '#FFFAFF';
export const RED_50 = '#FEF3F6';
export const RED_100 = '#FEE4E8';
export const RED_200 = '#FECDDD';
export const RED_300 = '#FDA1A4';
export const RED_400 = '#F96C73';
export const RED_500 = '#DA1C1C';
export const RED_600 = '#C60A0D';
export const RED_700 = '#B2010A';
export const RED_800 = '#9D2235';
export const RED_900 = '#620707';
export const RED_950 = '#2F0A0A';

export const ORANGE_25 = '#FFFDF7';
export const ORANGE_50 = '#FFFAEC';
export const ORANGE_100 = '#FFF0CA';
export const ORANGE_200 = '#FEE5A5';
export const ORANGE_300 = '#FFD468';
export const ORANGE_400 = '#FFC019';
export const ORANGE_500 = '#EAAA00';
export const ORANGE_600 = '#ED8B00';
export const ORANGE_700 = '#E26A00';
export const ORANGE_800 = '#DC4405';
export const ORANGE_900 = '#872902';
export const ORANGE_950 = '#481500';

export const BLUE_25 = '#F7FCFF';
export const BLUE_50 = '#ECF9FF';
export const BLUE_100 = '#CFEDFF';
export const BLUE_200 = '#A1DDFF';
export const BLUE_300 = '#73CBFE';
export const BLUE_400 = '#13A3F7';
export const BLUE_500 = '#0093E8';
export const BLUE_600 = '#0076BA';
export const BLUE_700 = '#00629B';
export const BLUE_800 = '#004269';
export const BLUE_900 = '#002C46';
export const BLUE_950 = '#000B2A';

export const ERROR_25 = '#FFFAF9';
export const ERROR_50 = '#FEF2F2';
export const ERROR_100 = '#FEE2E2';
export const ERROR_200 = '#FECACA';
export const ERROR_300 = '#FD9B99';
export const ERROR_400 = '#F96662';
export const ERROR_500 = '#FF3332';
export const ERROR_600 = '#EB0706';
export const ERROR_700 = '#B90000';
export const ERROR_800 = '#950000';
export const ERROR_900 = '#710000';
export const ERROR_950 = '#1F0001';

export const WARNING_25 = '#FFFDF5';
export const WARNING_50 = '#FFFAEB';
export const WARNING_100 = '#FEF0C7';
export const WARNING_200 = '#FEDF89';
export const WARNING_300 = '#FEC84B';
export const WARNING_400 = '#FDB022';
export const WARNING_500 = '#F79009';
export const WARNING_600 = '#DC6803';
export const WARNING_700 = '#B54708';
export const WARNING_800 = '#93370D';
export const WARNING_900 = '#7A2E0E';
export const WARNING_950 = '#1C0902';

export const SUCCESS_25 = '#F6FEF9';
export const SUCCESS_50 = '#ECFDF3';
export const SUCCESS_100 = '#D1FADF';
export const SUCCESS_200 = '#A6F4C5';
export const SUCCESS_300 = '#6CE9A6';
export const SUCCESS_400 = '#32D583';
export const SUCCESS_500 = '#12B76A';
export const SUCCESS_600 = '#039855';
export const SUCCESS_700 = '#027A48';
export const SUCCESS_800 = '#05603A';
export const SUCCESS_900 = '#054F31';
export const SUCCESS_950 = '#022919';

// Brand Pink ramp, no tone 500.
export const PINK_300 = '#F3BFCF';
export const PINK_400 = '#F88EAF';
export const PINK_600 = '#FF1F64';
export const PINK_700 = '#E40046';
export const PINK_800 = '#C70039';
export const PINK_900 = '#A2002A';

export const BLUE_SCALE = [
    BLUE_25,
    BLUE_50,
    BLUE_100,
    BLUE_200,
    BLUE_300,
    BLUE_400,
    BLUE_500,
    BLUE_600,
    BLUE_700,
    BLUE_800,
    BLUE_900,
    BLUE_950,
];

export const ERROR_SCALE = [
    ERROR_25,
    ERROR_50,
    ERROR_100,
    ERROR_200,
    ERROR_300,
    ERROR_400,
    ERROR_500,
    ERROR_600,
    ERROR_700,
    ERROR_800,
    ERROR_900,
    ERROR_950,
];

// Purple scale: categorical accent for multi-slice charts (e.g. the pie/donut
// KPI form). Light (25) → dark (950), mirroring the other scales.
export const PURPLE_25 = '#FBFAFF';
export const PURPLE_50 = '#F5F3FF';
export const PURPLE_100 = '#EDE9FE';
export const PURPLE_200 = '#DDD6FE';
export const PURPLE_300 = '#C4B5FD';
export const PURPLE_400 = '#A78BFA';
export const PURPLE_500 = '#8B5CF6';
export const PURPLE_600 = '#7C3AED';
export const PURPLE_700 = '#6D28D9';
export const PURPLE_800 = '#5B21B6';
export const PURPLE_900 = '#4C1D95';
export const PURPLE_950 = '#2E1065';

export const PURPLE_SCALE = [
    PURPLE_25,
    PURPLE_50,
    PURPLE_100,
    PURPLE_200,
    PURPLE_300,
    PURPLE_400,
    PURPLE_500,
    PURPLE_600,
    PURPLE_700,
    PURPLE_800,
    PURPLE_900,
    PURPLE_950,
];

/**
 * Ordered dark→light purple ramp for donut slices. Distinct enough for a
 * handful of categories; `generatePalette` extends it (by lightening) when a
 * chart has more slices than entries.
 */
export const PURPLE_DONUT_RAMP = [
    PURPLE_700,
    PURPLE_500,
    PURPLE_400,
    PURPLE_300,
    PURPLE_200,
    PURPLE_100,
];

/**
 * Categorical line colors for the generic multiline chart: four distinct
 * hues, not shades of one, so crossing lines stay distinguishable on a
 * white card. `generatePalette` extends it by lightening past four.
 */
export const MULTILINE_CHART_COLORS = [
    BLUE_700,
    RED_700,
    SUCCESS_700,
    PURPLE_700,
];

/**
 * Positional palette for the `campaign` chart palette (`bars`/`columns`/
 * `multiline`): orange first, red second. `generatePalette` extends it past
 * two series.
 */
export const CAMPAIGN_CHART_COLORS = [ORANGE_700, RED_600];

// Standalone brand colors for the App-downloads donuts (no matching design-token
// scale), promoted from raw hexes so the palettes below read as named tokens.
export const LIME_GREEN = '#BBDB44';
export const FOREST_GREEN = '#4E7A27';
export const MAROON = '#7A2E1F';

// Standalone brand colors for the milestone choice bars (no matching
// design-token scale), same precedent as LIME_GREEN above.
export const PINK = '#F06FA7';
export const MAGENTA = '#C2185B';

/**
 * Positional palette for the milestone choice bars: one color per coupon
 * option. `generatePalette` extends it past seven options.
 */
export const CHOICE_BAR_COLORS = [
    ORANGE_700,
    RED_800,
    MAGENTA,
    ORANGE_400,
    RED_400,
    PINK,
    BLUE_700,
];

/**
 * Donut colors for the App-downloads by-device pie, keyed by device label so iOS
 * always renders lime and Android forest green regardless of the envelope's row
 * order. A device not listed here falls back to the default donut ramp.
 */
export const DEVICE_DONUT_COLORS: Record<string, string> = {
    iOS: LIME_GREEN,
    Android: FOREST_GREEN,
};

/**
 * Donut colors for the App-downloads by-app pie, keyed by app label so
 * Couche-Tard always renders maroon and Circle K orange regardless of the
 * envelope's row order. An app not listed here falls back to the default ramp.
 */
export const APP_DONUT_COLORS: Record<string, string> = {
    'Couche-Tard': MAROON,
    'Circle K': ORANGE_600,
};

/** Business-unit series colours, keyed by API label. */
export const REGION_COLORS: Record<string, string> = {
    Quebec: BLUE_500,
    Atlantic: ORANGE_700,
    Ontario: ORANGE_500,
    Western: BLUE_700,
    Unknown: GRAY_400,
};
